import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Eye,
  Camera,
  Video,
  FileText,
  Calendar,
  User,
  Heart,
  Download,
  Share2,
  MoreHorizontal,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { 
  useGetSponsorshipUpdatesQuery, 
  useDeleteSponsorshipUpdateMutation 
} from '../../store/slices/apiSlice';
import toast from 'react-hot-toast';


const SponsorshipUpdates: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [expandedUpdates, setExpandedUpdates] = useState<Set<number>>(new Set());

  // API hooks
  const { data: updatesResponse, isLoading, error, refetch } = useGetSponsorshipUpdatesQuery({
    page: 1,
    limit: 50,
    search: searchQuery,
    type: typeFilter,
    status: statusFilter
  });
  
  const [deleteUpdate] = useDeleteSponsorshipUpdateMutation();

  const updates = updatesResponse?.data?.updates || [];

  // Filter and sort updates
  const filteredUpdates = updates
    .filter(update => {
      const title = update.title || '';
      const childName = update.child?.name || '';
      const description = update.description || '';
      
      const matchesSearch = title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          childName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = typeFilter === 'all' || update.type === typeFilter;
      const matchesStatus = statusFilter === 'all' || (update.is_notification_sent ? 'published' : 'draft') === statusFilter;
      return matchesSearch && matchesType && matchesStatus;
    })
    .sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'title':
          comparison = (a.title || '').localeCompare(b.title || '');
          break;
        case 'childName':
          comparison = (a.child?.name || '').localeCompare(b.child?.name || '');
          break;
        case 'date':
          comparison = new Date(a.update_date || 0).getTime() - new Date(b.update_date || 0).getTime();
          break;
        case 'createdAt':
          comparison = new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();
          break;
        default:
          comparison = 0;
      }
      return sortOrder === 'desc' ? -comparison : comparison;
    });

  const handleDeleteUpdate = async (updateId: number) => {
    if (window.confirm('Are you sure you want to delete this update? This action cannot be undone.')) {
      try {
        await deleteUpdate({ update_id: updateId }).unwrap();
        toast.success('Update deleted successfully');
        refetch();
      } catch (error: any) {
        toast.error(error?.data?.message || 'Failed to delete update');
      }
    }
  };

  const handleToggleExpanded = (updateId: number) => {
    const newExpanded = new Set(expandedUpdates);
    if (newExpanded.has(updateId)) {
      newExpanded.delete(updateId);
    } else {
      newExpanded.add(updateId);
    }
    setExpandedUpdates(newExpanded);
  };

  const formatDate = (dateString: string | Date | null | undefined) => formatDateTimeUtil(dateString);;

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'progress_report':
        return <FileText className="w-5 h-5 text-blue-600" />;
      case 'photo_update':
        return <Camera className="w-5 h-5 text-green-600" />;
      case 'video_update':
        return <Video className="w-5 h-5 text-purple-600" />;
      case 'general_update':
        return <FileText className="w-5 h-5 text-orange-600" />;
      default:
        return <FileText className="w-5 h-5 text-gray-600" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'progress_report':
        return 'bg-blue-100 text-blue-800';
      case 'photo_update':
        return 'bg-green-100 text-green-800';
      case 'video_update':
        return 'bg-purple-100 text-purple-800';
      case 'general_update':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusBadge = (isNotificationSent: boolean) => {
    if (isNotificationSent) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Published
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          Draft
        </span>
      );
    }
  };

  const stats = {
    total: updates.length,
    published: updates.filter(u => u.is_notification_sent).length,
    drafts: updates.filter(u => !u.is_notification_sent).length,
    thisMonth: updates.filter(u => {
      const updateDate = new Date(u.update_date);
      const now = new Date();
      return updateDate.getMonth() === now.getMonth() && updateDate.getFullYear() === now.getFullYear();
    }).length
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/child-sponsorship')}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Sponsorship Updates</h1>
                <p className="text-gray-600">Manage progress updates and communications for sponsored children</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/admin/child-sponsorship/updates/create')}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Update
            </button>
          </div>
        </div>
      </div>

      <div className="px-6 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Updates</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <Eye className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Published</p>
                <p className="text-2xl font-bold text-gray-900">{stats.published}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Edit className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Drafts</p>
                <p className="text-2xl font-bold text-gray-900">{stats.drafts}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Calendar className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">This Month</p>
                <p className="text-2xl font-bold text-gray-900">{stats.thisMonth}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search updates by title, child name, or description..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex gap-4">
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Types</option>
                  <option value="progress_report">Progress Reports</option>
                  <option value="photo_update">Photo Updates</option>
                  <option value="video_message">Video Messages</option>
                  <option value="academic_report">Academic Reports</option>
                </select>

                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </select>

                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="date">Sort by Date</option>
                  <option value="title">Sort by Title</option>
                  <option value="childName">Sort by Child</option>
                  <option value="createdAt">Sort by Created</option>
                </select>

                <button
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {sortOrder === 'asc' ? '↑' : '↓'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Updates List */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">All Updates</h2>
          </div>
          <div className="divide-y divide-gray-200">
            {isLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <p className="text-red-600">Error loading updates: {error?.data?.message || 'Unknown error'}</p>
              </div>
            ) : filteredUpdates.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No updates found</h3>
                <p className="text-gray-600 mb-4">
                  {searchQuery || typeFilter !== 'all' || statusFilter !== 'all'
                    ? 'Try adjusting your search or filter criteria.'
                    : 'Get started by creating your first update.'}
                </p>
                <button
                  onClick={() => navigate('/admin/child-sponsorship/updates/create')}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Create Update
                </button>
              </div>
            ) : (
              filteredUpdates.map((update) => (
                <div key={update.update_id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        {getTypeIcon(update.type || '')}
                        <h3 className="text-lg font-semibold text-gray-900">{update.title || 'Untitled Update'}</h3>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(update.type || '')}`}>
                          {(update.type || 'general_update').replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                        {getStatusBadge(!!update.is_notification_sent)}
                      </div>
                      
                      <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                        <div className="flex items-center">
                          <User className="w-4 h-4 mr-1" />
                          {update.child?.name || 'Unknown Child'}
                        </div>
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          {update.update_date ? formatDate(update.update_date) : 'No Date'}
                        </div>
                      </div>
                      
                      <p className="text-gray-700 mb-4 line-clamp-2">{update.description || 'No description provided'}</p>
                      
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span>{update.images?.length || 0} image{(update.images?.length || 0) !== 1 ? 's' : ''}</span>
                        <span>{update.videos?.length || 0} video{(update.videos?.length || 0) !== 1 ? 's' : ''}</span>
                        <span>Created: {update.created_at ? formatDate(update.created_at) : 'Unknown'}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => handleToggleExpanded(update.update_id)}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        title={expandedUpdates.has(update.update_id) ? 'Collapse' : 'Expand'}
                      >
                        {expandedUpdates.has(update.update_id) ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => navigate(`/admin/child-sponsorship/${update.child.child_id}`)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="View Child"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => navigate(`/admin/child-sponsorship/updates/edit/${update.update_id}`)}
                        className="text-green-600 hover:text-green-700 p-2 hover:bg-green-50 rounded-lg transition-all duration-200 border border-transparent hover:border-green-300 hover:shadow-sm"
                        title="Edit Update"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteUpdate(update.update_id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete Update"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Expanded Content */}
                  {expandedUpdates.has(update.update_id) && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="space-y-4">
                        {update.images && update.images.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-900 mb-2">Images</h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              {update.images.map((image, index) => (
                                <div key={index} className="aspect-w-16 aspect-h-9 bg-gray-200 rounded-lg overflow-hidden">
                                  <img src={image} alt={`Update ${update.update_id} image ${index + 1}`} className="w-full h-full object-cover" />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {update.videos && update.videos.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-900 mb-2">Videos</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {update.videos.map((video, index) => (
                                <div key={index} className="aspect-w-16 aspect-h-9 bg-gray-200 rounded-lg overflow-hidden">
                                  <video src={video} controls className="w-full h-full" />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {update.content && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-900 mb-2">Content</h4>
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <p className="text-gray-700 whitespace-pre-wrap">{update.content}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SponsorshipUpdates;
